import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, defineMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import LoadingButton from '@sorare/core/src/atoms/buttons/LoadingButton';
import { TextInput } from '@sorare/core/src/atoms/inputs/TextInput';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineL, LabelS } from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog';
import { isTypeFn } from '@sorare/core/src/gql';
import usePaginatedQuery from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { GridBackground } from '@sorare/gameplay/src/components/rivals/GridBackground';

import { AvatarSelector } from 'components/rivalsUserGroup/AvatarSelector';

import {
  CreateRivalsSquadDialogQuery,
  CreateRivalsSquadDialogQueryVariables,
} from './__generated__/index.graphql';

const Foreground = styled(Vertical)`
  position: relative;
  isolation: isolate;
  padding: var(--double-unit);
`;
const AbsoluteLabelS = styled(LabelS)`
  position: absolute;
  top: calc(8 * var(--unit));
`;
const StyledGridBackground = styled(GridBackground)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const AvatarSelectorWrapper = styled.div`
  position: absolute;
  width: 100%;
  top: 50%;
  transform: translateY(-50%);
  padding: 0 var(--double-unit);
`;
const FooterButtons = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: var(--double-unit);
  @media ${tabletAndAbove} {
    position: static;
    padding-top: calc(8 * var(--unit));
  }
`;

const CREATE_RIVALS_SQUAD_DIALOG_QUERY = gql`
  query CreateRivalsSquadDialogQuery($after: String) {
    shopItems(
      types: [RIVALS_SQUAD_LOGO]
      first: 20
      after: $after
      sortType: { direction: ASC, type: NAME }
    ) {
      nodes {
        ... on ShopItem {
          id
          pictureUrl
          name
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
` as TypedDocumentNode<
  CreateRivalsSquadDialogQuery,
  CreateRivalsSquadDialogQueryVariables
>;

const placeholderMessage = defineMessage({
  id: 'CreateLeagueDialog.nameStep.placeholder',
  defaultMessage: 'Squad name',
});

type Props = {
  open: boolean;
  onClose: () => void;
  onNext: ({
    displayName,
    logoId,
  }: {
    displayName: string;
    logoId: string;
  }) => Promise<void>;
  loading: boolean;
};
export const CreateSquadDialog = ({
  open,
  onClose,
  onNext,
  loading,
}: Props) => {
  const { formatMessage } = useIntl();
  const [squadNameInput, setSquadNameInput] = useState<string>('');
  const [logoId, setLogoId] = useState<string>('');

  const { data: avatars, loadMore } = usePaginatedQuery(
    CREATE_RIVALS_SQUAD_DIALOG_QUERY,
    {
      connection: 'ClubShopItemConnection',
      skip: !open,
    }
  );
  const { nodes, pageInfo } = avatars?.shopItems || {};
  const { hasNextPage, endCursor } = pageInfo || {};
  const submitDisabled = !squadNameInput.trim() || !logoId;

  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      open={open}
      onClose={onClose}
      title={
        <HeadlineL className="text-center">
          <FormattedMessage
            id="CreateSquadDialog.nameStep.title"
            defaultMessage="Create a squad"
          />
        </HeadlineL>
      }
    >
      <Vertical gap={0}>
        <StyledGridBackground
          height={230}
          bgColor="var(--c-neutral-200)"
          color="#393939"
        >
          <AvatarSelectorWrapper>
            <AvatarSelector
              avatars={(nodes || []).filter(isTypeFn('ShopItem'))}
              onChange={({ id }) => setLogoId(id)}
              onLoadMore={() => {
                if (hasNextPage) {
                  loadMore(false, {
                    after: endCursor,
                  });
                }
              }}
            />
          </AvatarSelectorWrapper>
        </StyledGridBackground>
        <Foreground>
          <Vertical>
            <TextInput
              placeholder={formatMessage(placeholderMessage)}
              value={squadNameInput}
              onChange={e => {
                setSquadNameInput(e.currentTarget.value);
              }}
              maxLength={32}
            />
            {squadNameInput.length === 32 && (
              <AbsoluteLabelS color="var(--c-neutral-600)">
                <FormattedMessage
                  id="CreateSquadDialog.maxLength"
                  defaultMessage="Max 32 characters"
                />
              </AbsoluteLabelS>
            )}
          </Vertical>
        </Foreground>
      </Vertical>
      <FooterButtons>
        <LoadingButton
          size="large"
          color={submitDisabled ? 'tertiary' : 'primary'}
          fullWidth
          onClick={() => {
            onNext({ displayName: squadNameInput.trim(), logoId });
          }}
          disabled={submitDisabled}
          loading={loading}
        >
          <FormattedMessage {...glossary.next} />
        </LoadingButton>
      </FooterButtons>
    </Dialog>
  );
};
